import { useState, useMemo, useEffect, useCallback } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Edit3, Trash2 } from 'lucide-react'
import { useOrganization } from '../contexts/OrganizationContext'
import { useCanAddCreators } from '../hooks/useCanAddCreators'
import { useCanManageCreators } from '../hooks/useCanManageCreators'
import { creatorApi } from '../services/api'
import {
  Alert,
  Button,
  ConfirmDialog,
  DataTable,
  Dialog,
  Field,
  FileUploadField,
  IconButton,
  Input,
  PageHeader,
  Pagination,
  PageLayout,
  PageToolbar,
  SearchField,
  StatePanel,
} from './common'
import styles from './CreatorManager.module.css'
import CreatorCreateDialog from './CreatorCreateDialog'

// An organization user's list holds only the organization's creators (the
// ones assigned to it alone, whoever added them); creators shared with other
// books stay with Wager staff.
function emptyCreatorsDescription(canCreateHere, canAdd) {
  if (canCreateHere) return 'Add the first creator for this organization.'
  if (canAdd) return 'Add a creator from the creator menu when you create a post.'
  return 'This organization has no creators of its own yet.'
}

function CreatorManager() {
  const { currentOrg } = useOrganization()
  const canManage = useCanManageCreators()
  const canAdd = useCanAddCreators()
  // Creating needs Feed publish (the API's gate) on top of managing, so a
  // configure-only admin is not offered a dialog the API would refuse.
  const canCreateHere = canManage && canAdd
  const queryClient = useQueryClient()
  const [message, setMessage] = useState({ type: '', text: '' })
  const [showAddDialog, setShowAddDialog] = useState(false)
  const [editingCreator, setEditingCreator] = useState(null)
  const [editDisplayName, setEditDisplayName] = useState('')
  const [avatarPreview, setAvatarPreview] = useState(null)
  const [avatarFile, setAvatarFile] = useState(null)
  const [avatarUploading, setAvatarUploading] = useState(false)
  const [confirmAction, setConfirmAction] = useState(null)
  const [searchTerm, setSearchTerm] = useState('')
  const [page, setPage] = useState(1)

  // Cleanup blob URLs when modal closes
  useEffect(() => {
    if (!editingCreator && avatarPreview && avatarFile) {
      URL.revokeObjectURL(avatarPreview)
      setAvatarPreview(null)
      setAvatarFile(null)
    }
  }, [editingCreator]) // eslint-disable-line react-hooks/exhaustive-deps

  const pageSize = 25
  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ['creators', currentOrg?.slug, searchTerm, page],
    queryFn: () => creatorApi.search(currentOrg.slug, searchTerm, {
      limit: pageSize,
      offset: (page - 1) * pageSize,
    }),
    enabled: !!currentOrg?.slug,
    placeholderData: (previousData) => previousData,
  })

  const creators = useMemo(() => {
    return (data?.creators || []).map(c => ({
      id: c.id,
      display_name: c.display_name || 'Unnamed',
      avatar_url: c.avatar_url,
    }))
  }, [data])
  const totalCreators = data?.pagination?.total ?? creators.length
  const pageCount = Math.max(1, Math.ceil(totalCreators / pageSize))

  useEffect(() => {
    setPage(1)
  }, [currentOrg?.slug])

  useEffect(() => {
    if (data?.pagination && page > pageCount) setPage(pageCount)
  }, [data?.pagination, page, pageCount])


  const removeMutation = useMutation({
    mutationFn: ({ userId }) => creatorApi.remove(currentOrg.slug, userId),
    onSuccess: () => {
      setMessage({ type: 'success', text: 'Creator removed from organization' })
      queryClient.invalidateQueries({ queryKey: ['creators', currentOrg?.slug] })
    },
    onError: (err) => setMessage({ type: 'error', text: err.message || 'Failed to remove creator' }),
  })

  const updateMutation = useMutation({
    mutationFn: ({ creatorId, updates }) => creatorApi.update(currentOrg.slug, creatorId, updates),
    onSuccess: () => {
      setMessage({ type: 'success', text: 'Creator updated successfully' })
      setEditingCreator(null)
      queryClient.invalidateQueries({ queryKey: ['creators', currentOrg?.slug] })
    },
    onError: (err) => setMessage({ type: 'error', text: err.response?.data?.error || err.message || 'Failed to update creator' }),
  })

  const openEditModal = useCallback((creator) => {
    setEditDisplayName(creator.display_name || '')
    setAvatarPreview(creator.avatar_url || null)
    setAvatarFile(null)
    setEditingCreator(creator)
  }, [])

  const closeEditModal = () => {
    if (avatarFile && avatarPreview) {
      URL.revokeObjectURL(avatarPreview)
    }
    setAvatarPreview(null)
    setAvatarFile(null)
    setEditingCreator(null)
  }


  const handleAvatarFileChange = (e) => {
    const file = e.target.files?.[0]
    if (!file) return

    const allowed = ['image/jpeg', 'image/png', 'image/webp', 'image/gif']
    if (!allowed.includes(file.type)) {
      setMessage({ type: 'error', text: 'Only JPEG, PNG, WebP, and GIF images are allowed' })
      return
    }

    // Revoke old blob URL if one exists from a previous file pick
    if (avatarFile && avatarPreview) {
      URL.revokeObjectURL(avatarPreview)
    }

    setAvatarFile(file)
    setAvatarPreview(URL.createObjectURL(file))
    e.target.value = ''
  }

  const handleRemoveAvatar = () => {
    if (avatarFile && avatarPreview) {
      URL.revokeObjectURL(avatarPreview)
    }
    setAvatarFile(null)
    setAvatarPreview(null)
  }

  const handleSaveEdit = async () => {
    if (!editingCreator) return
    setAvatarUploading(true)

    try {
      let newAvatarUrl = null

      // Upload avatar if a new file was selected
      if (avatarFile) {
        const { avatar_url } = await creatorApi.uploadAvatar(
          currentOrg.slug,
          editingCreator.id,
          avatarFile
        )
        newAvatarUrl = avatar_url
      }

      // Check if display name changed
      const nameChanged = editDisplayName !== (editingCreator.display_name || '')
      // Check if avatar was removed (had one before, now null, and no new file)
      const avatarRemoved = !avatarFile && !avatarPreview && editingCreator.avatar_url

      if (nameChanged || avatarRemoved) {
        const updates = {}
        if (nameChanged) updates.display_name = editDisplayName
        if (avatarRemoved) updates.avatar_url = ''
        updateMutation.mutate({ creatorId: editingCreator.id, updates })
      } else if (newAvatarUrl) {
        // Avatar was uploaded via dedicated endpoint which already updates DB
        queryClient.invalidateQueries({ queryKey: ['creators', currentOrg?.slug] })
        setMessage({ type: 'success', text: 'Creator updated successfully' })
        setEditingCreator(null)
      } else {
        // Nothing changed
        setEditingCreator(null)
      }
    } catch (err) {
      setMessage({ type: 'error', text: err.response?.data?.error || err.message || 'Failed to update creator' })
    } finally {
      setAvatarUploading(false)
    }
  }

  const columns = useMemo(() => {
    const nameColumn = {
      key: 'display_name',
      label: 'Name',
      sortable: true,
      render: (row) => (
        <div className={styles.creatorCell}>
          {row.avatar_url ? (
            <img src={row.avatar_url} alt="" className={styles.avatar} />
          ) : (
            <div className={styles.avatarPlaceholder}>
              {(row.display_name || '?').charAt(0).toUpperCase()}
            </div>
          )}
          <span>{row.display_name}</span>
        </div>
      ),
    }
    if (!canManage) return [nameColumn]
    return [nameColumn, {
      key: 'actions',
      label: '',
      render: (row) => (
        <div className={styles.actionButtons}>
          <IconButton
            type="button"
            variant="ghost"
            onClick={(e) => {
              e.stopPropagation()
              openEditModal(row)
            }}
            disabled={updateMutation.isPending}
            label="Edit creator"
          >
            <Edit3 size={16} />
          </IconButton>
          <IconButton
            type="button"
            variant="danger"
            className={styles.removeButton}
            onClick={(e) => {
              e.stopPropagation()
              setConfirmAction({
                title: 'Remove Creator',
                message: `Remove ${row.display_name} from this organization?`,
                variant: 'danger',
                confirmLabel: 'Remove',
                onConfirm: () => {
                  removeMutation.mutate({ userId: row.id })
                  setConfirmAction(null)
                }
              })
            }}
            disabled={removeMutation.isPending}
            label="Remove creator"
          >
            <Trash2 size={16} />
          </IconButton>
        </div>
      ),
    }]
  }, [canManage, removeMutation, updateMutation, openEditModal])

  const isSaving = updateMutation.isPending || avatarUploading

  const handleCreated = useCallback((_creator, { avatarUploadFailed } = {}) => {
    setMessage(avatarUploadFailed
      ? { type: 'error', text: 'Creator added but avatar upload failed' }
      : { type: 'success', text: 'Creator added to organization' })
    queryClient.invalidateQueries({ queryKey: ['creators', currentOrg?.slug] })
  }, [currentOrg?.slug, queryClient])

  if (!currentOrg?.slug) {
    return (
      <PageLayout size="wide">
        <PageHeader title="Creators" />
        <StatePanel
          state="restricted"
          title="No organization selected"
          description="Pick an organization to manage its creators."
        />
      </PageLayout>
    )
  }

  return (
    <PageLayout size="wide">
      <PageHeader
        title="Creators"
        context={currentOrg?.name || currentOrg.slug}
        primaryAction={canCreateHere ? (
          <Button onClick={() => setShowAddDialog(true)}>Add creator</Button>
        ) : null}
      />

      {message.text && (
        <Alert variant={message.type === 'error' ? 'error' : 'success'}>
          {message.text}
        </Alert>
      )}

      {isLoading ? (
        <StatePanel state="loading" title="Loading creators…" />
      ) : isError ? (
        <StatePanel
          state="error"
          title="Creators could not load"
          description="The creator roster was not changed."
          onRetry={refetch}
        />
      ) : totalCreators === 0 && !searchTerm.trim() ? (
        <StatePanel
          state="empty"
          title="No creators yet"
          description={emptyCreatorsDescription(canCreateHere, canAdd)}
        />
      ) : (
        <>
          <PageToolbar
            label="Creator controls"
            search={
              <SearchField
                label="Search creators"
                placeholder="Search creators…"
                value={searchTerm}
                onChange={(value) => {
                  setSearchTerm(value)
                  setPage(1)
                }}
              />
            }
          />
          {creators.length === 0 ? (
            <StatePanel
              state="search-empty"
              title="No creators match"
              description={`No creators match “${searchTerm.trim()}”.`}
              onClear={() => {
                setSearchTerm('')
                setPage(1)
              }}
            />
          ) : (
            <>
              <DataTable
                label="Creators"
                data={creators}
                columns={columns}
                density="compact"
              />
              <Pagination
                page={page}
                pageCount={pageCount}
                onPageChange={setPage}
                label="Creator pages"
              />
            </>
          )}
        </>
      )}

      <ConfirmDialog
        open={!!confirmAction}
        title={confirmAction?.title || ''}
        message={confirmAction?.message || ''}
        variant={confirmAction?.variant || 'default'}
        confirmLabel={confirmAction?.confirmLabel || 'Confirm'}
        onConfirm={confirmAction?.onConfirm || (() => {})}
        onCancel={() => setConfirmAction(null)}
      />

      <CreatorCreateDialog
        open={showAddDialog}
        onClose={() => setShowAddDialog(false)}
        onCreated={handleCreated}
      />


      <Dialog
        open={!!editingCreator}
        title="Edit creator"
        description="Update the creator name or replace the avatar shown across Studio."
        onClose={closeEditModal}
        footer={
          <>
            <Button variant="secondary" onClick={closeEditModal}>
              Cancel
            </Button>
            <Button
              onClick={handleSaveEdit}
              disabled={isSaving || !editDisplayName.trim()}
            >
              {isSaving ? 'Saving...' : 'Save Changes'}
            </Button>
          </>
        }
      >
        <div className={styles.modalStack}>
          <FileUploadField
            label="Avatar"
            variant="avatar"
            accept="image/jpeg,image/png,image/webp,image/gif"
            onChange={handleAvatarFileChange}
            triggerLabel="Choose Image"
            filename={avatarFile?.name}
            previewSrc={avatarPreview}
            placeholderText={(editDisplayName || '?').charAt(0).toUpperCase()}
            onClear={avatarPreview ? handleRemoveAvatar : undefined}
            helper="Replace the current image or remove it to fall back to initials."
            pending={avatarUploading}
          />
          <Field label="Display name" required>
            <Input
              value={editDisplayName}
              onChange={(event) => setEditDisplayName(event.target.value)}
              placeholder="Enter display name"
              data-autofocus
            />
          </Field>
        </div>
      </Dialog>
    </PageLayout>
  )
}

export default CreatorManager
